import {ConnectingEditor, TextEditorServices} from '@frontapp/editor-html';
import React, {FC, RefObject} from 'react';
import styled, {css} from 'styled-components';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {
  FrontClickEvent,
  FrontFocusEvent,
  FrontKeyboardEvent,
  FrontMouseEvent,
  Handler,
  HandlerOf,
  MouseEventHandler,
  RendererOf,
} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {boxedInputColorReaders} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {markFullStorySensitive} from '../../../helpers/fullstoryHelpers';
import {ContentEditable} from '../editor/contentEditable';
import {ServiceSubscription} from '../editor/serviceSubscription';

/*
 * Constants.
 */

const borderRadius = 8;
const borderWidth = 2;
export const searchTokenClassName = 'front-decoration-search-token';

/*
 * Props.
 */

interface SearchBarProps {
  editor: ConnectingEditor<HTMLDivElement, TextEditorServices>;
  outerRef?: RefObject<HTMLDivElement>;

  isFocused?: boolean;
  isDisabled?: boolean;

  onKeyDown: HandlerOf<FrontKeyboardEvent>;
  onBlur?: HandlerOf<FrontFocusEvent<HTMLDivElement>>;
  onMouseDown?: HandlerOf<FrontMouseEvent>;
  onClick?: HandlerOf<FrontClickEvent>;
  onFocus?: Handler;

  onExitMouseDown?: HandlerOf<FrontMouseEvent>;
  onExitClick?: Handler;

  renderPlaceholder: RendererOf<boolean>;
  renderLeftContent?: RendererOf<boolean>;
  renderRightContent?: RendererOf<boolean>;
}

interface SearchBarWrapperStyleProps {
  $isDisabled?: boolean;
  $hasFocus?: boolean;
}

/*
 * Styles.
 */

const StyledSearchBarP0WrapperDiv = styled.div`
  // Disable drag in Electron.
  -webkit-app-region: no-drag;

  grid-area: search;
  justify-self: center;
  width: 100%;
`;

const StyledSearchBarWrapperDiv = styled.div`
  position: relative;
`;

const StyledOuterSearchBarWrapperDiv = styled.div<SearchBarWrapperStyleProps>`
  /* Renders a center border. Unlike an inset box-shadow, this renders on top of the content. */
  &::after {
    content: '';

    position: absolute;
    top: -${borderWidth / 2}px;
    left: -${borderWidth / 2}px;
    width: calc(100% + ${borderWidth}px);
    height: calc(100% + ${borderWidth}px);

    border-radius: ${borderRadius + borderWidth / 2}px;
    ${(p) => addOuterWrapperBorderStyles(p)}

    pointer-events: none;
  }
`;

const StyledInnerSearchBarWrapperDiv = styled.div<SearchBarWrapperStyleProps>`
  position: relative;

  display: grid;
  grid-template-areas: 'left . content . right';
  grid-template-columns: auto 8px 1fr 8px auto;

  width: 100%;
  overflow: hidden;
  border-radius: 8px;
  background-color: ${(p) => p.theme.alphas.white30};
  align-items: stretch;

  ${(p) => maybeAddInnerWrapperFocusStyles(p)}
  ${(p) => maybeAddInnerWrapperDisabledStyles(p)}
`;

const StyledContentEditable = styled(ContentEditable)<SearchBarWrapperStyleProps>`
  flex: 1;

  ${addEditorBoxStyles()}
  ${addContentEditableStyles()}

  .${searchTokenClassName} {
    background-color: ${(p) =>
      p.$hasFocus ? p.theme.palette[PaletteColorsEnum.BLUE].shade20 : p.theme.alphas.black50};

    border-radius: 4px;
    font-weight: ${fontWeights.medium};
    padding: 2px 6px 3px;
  }
`;

const StyledPlaceholderDiv = styled.div<SearchBarWrapperStyleProps>`
  ${(p) =>
    p.$hasFocus &&
    // Set the position to absolute when focused to let the content editable grow.
    css`
      position: absolute;
    `}

  cursor: text;
  pointer-events: none;
  color: ${(p) => (p.$hasFocus ? boxedInputColorReaders.textPlaceholder : p.theme.alphas.white40)};
  ${addContentEditableStyles()}
  ${ellipsis()}
`;

const StyledLeftContentWrapperDiv = styled.div`
  grid-area: left;

  display: flex;
`;

const StyledIcon = styled(Icon)`
  margin: 8px 0 0 10px;
`;

const StyledContentEditableWrapperDiv = styled.div<SearchBarWrapperStyleProps>`
  grid-area: content;

  flex: 1;
  display: flex;
  align-items: center;

  overflow-x: hidden;
  white-space: nowrap;

  color: ${(p) => (p.$hasFocus ? p.theme.greys.shade90 : p.theme.greys.white)};
  font-size: ${fontSizes.medium};
`;

const StyledRightContentWrapperDiv = styled.div`
  grid-area: right;

  display: flex;

  padding: 4px;
`;

export const StyledSearchBarRightButton = styled(ChromelessButton)`
  display: flex;
  align-items: center;

  padding: 4px;
  border-radius: 6px;

  &:not(:last-of-type) {
    margin-right: 4px;
  }
`;

/*
 * Style Helpers.
 */

function addEditorBoxStyles() {
  return css`
    div[data-key] {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `;
}

function addContentEditableStyles() {
  return css`
    line-height: 22px;
    padding: 5px 0px;
  `;
}

function addOuterWrapperBorderStyles(props: SearchBarWrapperStyleProps) {
  if (props.$isDisabled) {
    return css`
      border: ${borderWidth}px solid ${(p) => p.theme.alphas.white30};
    `;
  }

  if (props.$hasFocus) {
    return css`
      border: ${borderWidth}px solid ${(p) => p.theme.palette.blue.shade40};
    `;
  }

  return css`
    border: ${borderWidth}px solid transparent;
  `;
}

function maybeAddInnerWrapperFocusStyles(props: SearchBarWrapperStyleProps) {
  if (!props.$hasFocus) {
    return '';
  }

  return css`
    background-color: ${(p) => p.theme.greys.white};
  `;
}

function maybeAddInnerWrapperDisabledStyles(props: SearchBarWrapperStyleProps) {
  if (!props.$isDisabled) {
    return '';
  }

  return css`
    opacity: 0.5;
    background-color: transparent;

    user-select: none;
    cursor: not-allowed;
  `;
}

/*
 * Component.
 */

export const SearchBar: FC<SearchBarProps> = (props) => {
  const {editor} = props;

  const focusSearch = () => {
    editor.services.focus.focus();
    editor.services.selection.selectAll();

    props.onFocus?.();
  };

  /*
   * Event handlers.
   */

  const onDoubleClick: MouseEventHandler = (event) => {
    // Don't maximize in Electron.
    event.preventDefault();
  };

  /*
   * Render.
   */

  const keyHandlers = {
    [KeyboardActionsEnum.SEARCH]: focusSearch,
  };

  return (
    <StyledSearchBarP0WrapperDiv
      onDoubleClick={onDoubleClick}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buildInteractionIdProps('p0-search-bar')}
    >
      <KeyboardShortcuts handlers={keyHandlers}>
        <ServiceSubscription source={editor.services.empty}>
          {({isEmpty}) => renderSearchBar(props, isEmpty)}
        </ServiceSubscription>
      </KeyboardShortcuts>
    </StyledSearchBarP0WrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderSearchBar(props: SearchBarProps, isEmpty: boolean) {
  const {
    isDisabled,
    isFocused,
    editor,
    outerRef,
    renderPlaceholder,
    renderLeftContent,
    renderRightContent,
    onKeyDown,
    onBlur,
    onMouseDown,
    onClick,
    onExitMouseDown,
    onExitClick,
  } = props;

  return (
    <StyledSearchBarWrapperDiv role="presentation" onKeyDown={onKeyDown} spellCheck={false}>
      <StyledOuterSearchBarWrapperDiv
        ref={outerRef}
        $isDisabled={isDisabled}
        $hasFocus={isFocused}
        onBlur={onBlur}
      >
        <StyledInnerSearchBarWrapperDiv $isDisabled={isDisabled} $hasFocus={isFocused}>
          {maybeRenderLeftContent(isEmpty, Boolean(isFocused), renderLeftContent)}
          <StyledContentEditableWrapperDiv
            role="search"
            $isDisabled={isDisabled}
            $hasFocus={isFocused}
            onMouseDown={onMouseDown}
            onClick={onClick}
            {...buildInteractionIdProps('p0-search-bar-text')}
          >
            {maybeRenderPlaceholder(isEmpty, isFocused, renderPlaceholder)}
            <StyledContentEditable
              editor={editor}
              $isDisabled={isDisabled}
              $hasFocus={isFocused}
              className={markFullStorySensitive()}
            />
          </StyledContentEditableWrapperDiv>
          <StyledRightContentWrapperDiv>
            {maybeRenderExitButton(isEmpty, isFocused, onExitMouseDown, onExitClick)}
            {maybeRenderRightContent(isEmpty, renderRightContent)}
          </StyledRightContentWrapperDiv>
        </StyledInnerSearchBarWrapperDiv>
      </StyledOuterSearchBarWrapperDiv>
    </StyledSearchBarWrapperDiv>
  );
}

function maybeRenderPlaceholder(
  isEmpty: boolean,
  isFocused?: boolean,
  renderPlaceholder?: RendererOf<boolean>,
) {
  if (!renderPlaceholder || !isEmpty) {
    return null;
  }

  return (
    <StyledPlaceholderDiv $hasFocus={isFocused}>{renderPlaceholder(Boolean(isFocused))}</StyledPlaceholderDiv>
  );
}

function maybeRenderLeftContent(
  isEmpty: boolean,
  isFocused?: boolean,
  renderLeftContent?: RendererOf<boolean>,
) {
  // If `renderLeftContent` returns `null`, we're displaying the search icon.
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledLeftContentWrapperDiv>
          {renderLeftContent?.(isEmpty) || (
            <StyledIcon name="searchSmall" color={isFocused ? theme.greys.shade70 : theme.greys.white} />
          )}
        </StyledLeftContentWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderExitButton(
  isEmpty: boolean,
  isFocused?: boolean,
  onExitMouseDown?: HandlerOf<FrontMouseEvent>,
  onExitClick?: Handler,
) {
  if (isEmpty || (!onExitMouseDown && !onExitClick)) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledSearchBarRightButton onMouseDown={onExitMouseDown} onClick={onExitClick}>
          <Icon
            name="crossCircleSmall"
            color={isFocused ? theme.greys.shade60 : theme.greys.white}
            hoverColor={isFocused ? theme.greys.shade70 : theme.greys.shade30}
          />
        </StyledSearchBarRightButton>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderRightContent(isEmpty: boolean, renderRightContent?: RendererOf<boolean>) {
  if (!renderRightContent) {
    return null;
  }

  return renderRightContent(isEmpty);
}
